import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent
} from "react";
import type { HoldState } from "../../types";

export function HoldControl({
  hold,
  maxHoldMs,
  disabled,
  onStart,
  onHeartbeat,
  onStop
}: {
  hold: HoldState | null;
  maxHoldMs: number;
  disabled: boolean;
  onStart: () => void;
  onHeartbeat: (holdId: string) => void;
  onStop: () => void;
}) {
  const pressed = useRef(false);
  const instructionId = useId();
  const [elapsed, setElapsed] = useState(0);
  const holdId = hold?.id ?? null;
  const holdStartedAt = hold?.startedAt ?? null;

  const stop = useCallback(() => {
    if (!pressed.current && !hold) return;
    pressed.current = false;
    onStop();
  }, [holdId, onStop]);

  const start = useCallback(() => {
    if (disabled || pressed.current) return;
    pressed.current = true;
    onStart();
  }, [disabled, onStart]);

  useEffect(() => {
    if (!holdId || holdStartedAt === null) {
      setElapsed(0);
      return;
    }
    const update = () =>
      setElapsed(Math.min(maxHoldMs, Date.now() - holdStartedAt));
    update();
    const timer = window.setInterval(update, 50);
    return () => window.clearInterval(timer);
  }, [holdId, holdStartedAt, maxHoldMs]);

  useEffect(() => {
    if (!holdId || !pressed.current) return;
    onHeartbeat(holdId);
    const timer = window.setInterval(() => onHeartbeat(holdId), 250);
    return () => window.clearInterval(timer);
  }, [holdId, onHeartbeat]);

  useEffect(() => {
    const stopOnBlur = () => stop();
    const stopWhenHidden = () => {
      if (document.visibilityState !== "visible") stop();
    };
    window.addEventListener("blur", stopOnBlur);
    window.addEventListener("pagehide", stopOnBlur);
    document.addEventListener("visibilitychange", stopWhenHidden);
    return () => {
      window.removeEventListener("blur", stopOnBlur);
      window.removeEventListener("pagehide", stopOnBlur);
      document.removeEventListener("visibilitychange", stopWhenHidden);
    };
  }, [stop]);

  function pointerDown(event: PointerEvent<HTMLButtonElement>) {
    if (typeof event.button === "number" && event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    start();
  }

  function pointerStop(event: PointerEvent<HTMLButtonElement>) {
    event.preventDefault();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    stop();
  }

  function keyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if ((event.key === " " || event.key === "Enter") && !event.repeat) {
      event.preventDefault();
      start();
    }
  }

  function keyUp(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      stop();
    }
  }

  const progress = maxHoldMs > 0 ? elapsed / maxHoldMs : 0;
  const remaining = Math.max(0, maxHoldMs - elapsed);

  return (
    <div
      className={`hold-control ${hold ? "hold-control--active" : ""}`}
      style={{ "--hold-progress": `${progress * 360}deg` } as React.CSSProperties}
    >
      <div className="hold-control__remaining">
        {Math.ceil(remaining / 1_000)}s
      </div>
      <div className="hold-control__ring">
        <button
          type="button"
          disabled={disabled}
          onPointerDown={pointerDown}
          onPointerUp={pointerStop}
          onPointerCancel={pointerStop}
          onPointerLeave={pointerStop}
          onLostPointerCapture={stop}
          onKeyDown={keyDown}
          onKeyUp={keyUp}
          onContextMenu={(event) => event.preventDefault()}
          aria-describedby={instructionId}
          aria-pressed={Boolean(hold)}
        >
          <strong>{hold ? "HOLDING" : "PRESS & HOLD"}</strong>
          <span>Release to stop</span>
        </button>
      </div>
      <span id={instructionId} className="sr-only">
        Press and keep holding to send a simulated control intent. Releasing,
        changing tabs, or losing connection stops immediately.
      </span>
    </div>
  );
}
